import { Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { bpsToPercent, promotionLabel, type PromotionType } from '@/lib/marketing';

export interface PreviewItem {
  id: string;
  merchant_name?: string | null;
  category_name?: string | null;
  promo_title?: string | null;
  promo_subtitle?: string | null;
  promo_image_url?: string | null;
  cashback_bps?: number | null;
  display_order?: number | null;
}

/** Mobile home preview of a promotion block as it appears in HisobAPP. */
export default function HisobAppPreview({
  promotionType,
  items,
}: {
  promotionType: PromotionType;
  items: PreviewItem[];
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
        <Smartphone size={16} className="text-primary" />
        {t('mkt_preview.title')}
      </div>
      <div className="mx-auto w-full max-w-[320px] rounded-3xl border border-border bg-background p-3 shadow-sm">
        <p className="mb-2 px-1 text-sm font-semibold text-foreground">{promotionLabel[promotionType]}</p>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
            {t('mkt_preview.empty')}
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {items.map((it) => (
              <div key={it.id} className="min-w-[132px] max-w-[132px] rounded-xl border border-border bg-card p-2">
                <div className="mb-2 flex h-16 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  {it.promo_image_url ? (
                    <img src={it.promo_image_url} alt={it.promo_title || it.merchant_name || t('mkt_preview.promoAlt')} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-[10px] text-muted-foreground">{t('mkt_preview.noImage')}</span>
                  )}
                </div>
                <p className="truncate text-xs font-medium text-foreground">
                  {it.promo_title || it.category_name || it.merchant_name || '—'}
                </p>
                {it.promo_subtitle ? (
                  <p className="truncate text-[10px] text-muted-foreground">{it.promo_subtitle}</p>
                ) : it.merchant_name ? (
                  <p className="truncate text-[10px] text-muted-foreground">{it.merchant_name}</p>
                ) : null}
                {it.cashback_bps ? (
                  <p className="mt-1 text-[10px] font-semibold text-primary">{bpsToPercent(it.cashback_bps)} {t('mkt_preview.cashback')}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
